import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Zap, Sparkles, CheckCircle2 } from 'lucide-react';

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'stripe-pricing-table': any;
        }
    }
}

export default function Upgrade() {
    const navigate = useNavigate();

    return (
        <div className="flex-1 overflow-y-auto bg-dark p-6 sm:p-12 relative">
            {/* Background Accents */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="max-w-5xl mx-auto space-y-12 relative z-10">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                    <div className="space-y-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors group mb-4"
                        >
                            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                            <span className="text-xs uppercase font-bold tracking-widest">Return to diagnostics</span>
                        </button>
                        <h1 className="text-5xl font-black text-white uppercase tracking-tighter">
                            Upgrade to <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">DIY-PRO</span>
                        </h1>
                        <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
                            Unlock unlimited AI diagnostic queries, saved projects,
                            and priority access to our master technician reasoning engine beyond the 3-question free trial.
                        </p>
                    </div>
                </div>

                {/* Features Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        {
                            title: 'DIY-PRO: Level 1 Unlimited',
                            desc: 'Unlimited diagnostic sessions, project storage, and full access to Level 1 repair intelligence.',
                            icon: <Zap className="text-primary" />
                        },
                        {
                            title: 'DIY-PREMIUM: Level 2 Master',
                            desc: 'Advanced access to Level 2 master-tier engineering data, diagrams, and AR spatial guides.',
                            icon: <Sparkles className="text-accent" />
                        },
                        {
                            title: 'Encrypted Logic',
                            desc: 'Advanced privacy with our secured diagnostic logic pipeline protecting your car data.',
                            icon: <ShieldCheck className="text-secondary" />
                        }
                    ].map((f, i) => (
                        <div key={i} className="bg-slate/5 border border-white/5 p-6 rounded-2xl space-y-3">
                            <div className="p-3 bg-white/5 rounded-xl w-fit">{f.icon}</div>
                            <h3 className="text-white font-black text-sm uppercase tracking-wider">{f.title}</h3>
                            <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Stripe Pricing Table */}
                <div className="bg-dark/40 border border-slate/20 rounded-3xl p-2 sm:p-8 shadow-2xl overflow-hidden relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-50" />
                    <div className="relative">
                        <stripe-pricing-table
                            pricing-table-id="prctbl_1Solk9CXDvmxKfrenOQyFnFF"
                            publishable-key="pk_live_51JEMmMCXDvmxKfreuwIye2xPXnCQsn0R8zC4oReSUDeeIVOTGhpPThDgySuop659QOkz4mR8NNOauFdqydy7orMb00ApoSaVX4"
                        />
                    </div>
                </div>

                {/* Footer Note */}
                <div className="flex flex-col items-center gap-4 text-center pb-12">
                    <div className="flex items-center gap-2 text-gray-500">
                        <CheckCircle2 size={16} className="text-primary" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em]">Secure checkout via Stripe</span>
                    </div>
                    <p className="text-[10px] text-gray-700 max-w-sm uppercase tracking-widest font-bold">
                        Already have a subscription?
                        <a href="https://billing.stripe.com/p/login/9B614maCtcm9dp1enYgfu00" className="text-white hover:text-primary transition-colors ml-1">
                            Manage Billing
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
